import { useState } from 'react';
import { toast } from 'react-toastify';

import { BuilderConfirmModal } from '../../Settings/General/Statement/Builder/BuilderConfirmModal';
import { useGetSelectedOrganisation } from '../../../hooks/useGetSelectedOrganisation';
import { useUser } from '../../../hooks/useUser';
import { useGiveAssistantConsentMutation } from '../../../services/api/assistantApi/assistant';

/** Wraps the agent's enable toggle so the org is asked for AI consent the first time it turns the agent on */
export function useAgentEnabledToggle(setEnabled: (enabled: boolean) => Promise<unknown>) {
  const currentOrg = useGetSelectedOrganisation();
  const { data: user } = useUser();
  const [giveConsent, consentState] = useGiveAssistantConsentMutation();
  const [isConsentOpen, setIsConsentOpen] = useState(false);

  async function onToggle(enabled: boolean) {
    // The agent sends org data to our AI provider. Admin override sessions bypass this, like the statement builder
    if (enabled && currentOrg?.aiConsentGiven !== true && !user?.adminOverride) {
      setIsConsentOpen(true);

      return;
    }

    await setEnabled(enabled);
  }

  async function consentAndEnable() {
    if (!currentOrg) {
      return;
    }

    try {
      await giveConsent({ organisationId: currentOrg.id }).unwrap();
    }
    catch {
      toast.error(`Could not record your consent.`);

      return;
    }

    setIsConsentOpen(false);
    await setEnabled(true);
  }

  const consentModal = (
    <BuilderConfirmModal
      open={ isConsentOpen }
      title={ `Consent needed to use the AI agent` }
      message={ `We take AI and your data seriously, so we need your permission before the agent can use it. The agent will be turned on once you consent.` }
      confirmLabel={ `I consent on behalf of my organisation` }
      busy={ consentState.isLoading }
      onConfirm={ consentAndEnable }
      onCancel={ () => setIsConsentOpen(false) }
    />
  );

  return { onToggle, consentModal };
}
